import { additionalDocumentMapper } from '../mappers/additional-document.mapper';
import { courseStatusMapper } from '../mappers/course-status.mapper';
import { courseMapper } from '../mappers/course.mapper';
import { documentTypeMapper } from '../mappers/document-type.mapper';
import { licenseStatusMapper } from '../mappers/license-status.mapper';
import { lookupMapper } from '../mappers/lookup.mapper';
import { zipMapper } from '../mappers/zip.mapper';
import { AdditionalDocument } from '../models/additional-document';
import { CourseDetail } from '../models/course-detail';
import { CourseStatus } from '../models/course-status';
import { DocumentType } from '../models/document-type';
import { LicenseStatus } from '../models/license-status';
import { RegistrationSource } from '../models/registration-source';
import { UserAccountStatus } from '../models/user-account-status';
import { VISIBILITY_NUMERIC_VALUE, VISIBILITY_VALUE, Visibility } from '../models/visibility';
import { Zip } from '../models/zip';
import { appUrlsConfig } from './app-urls.config';
import { axiosService } from './axios.service';

export class LookupOptions {
  constructor(data) {
    this.isELearningRegistration = data.isELearningRegistration;
    this.isClassRegistration = data.isClassRegistration;
    this.isPayment = data.isPayment;
    this.orderId = data.orderId;
    this.divisionId = data.divisionId;
  }
}

export class CourseLookupOptions {
  constructor(data) {
    this.isRequiredDob = data.isRequiredDob;
  }
}

/**
 * Map course to lookup options.
 * @param {CourseLookupOptions} options Data to map.
 * @returns {object}
 */
function mapCourseLookupOptionsToDto(options) {
  return {
    type: options?.isRequiredDob ? 'required_dob' : undefined,
  };
}

/**
 * Prepare lookup params.
 * @param {LookupOptions} options Query params
 * @returns {object}
 */
function prepareLookupParams(options) {
  const params = {};
  if (options.isELearningRegistration) {
    params.registration_type = 'elearning';
    params.type = 'elearning';
  }
  if (options.isClassRegistration) {
    params.registration_type = 'training_class';
    params.type = 'training_class';
  }
  if (options.isPayment) {
    params.type = 'payment';
  }
  if (options.orderId) {
    params.order_id = options.orderId;
  }
  if (options.divisionId) {
    params.division_id = options.divisionId;
  }
  return params;
}

/**
 * Get document types.
 * @param {LookupOptions} options Lookup options
 * @returns {Promise<DocumentType[]>}
 */
async function getDocumentTypes(options) {
  const response = await axiosService.get(appUrlsConfig.lookup.documentTypes, { params: prepareLookupParams(options) });
  return response.data.map(type => documentTypeMapper.fromDto(type));
}

/**
 * Get course statuses.
 * @returns {Promise<CourseStatus[]>}
 */
async function getCourseStatuses() {
  const response = await axiosService.get(appUrlsConfig.lookup.courseStatuses);
  return response.data.map(status => courseStatusMapper.fromDto(status));
}

/**
 * Get additional documents.
 * @returns {Promise<AdditionalDocument[]>}
 */
async function getAdditionalDocuments() {
  const response = await axiosService.get(appUrlsConfig.lookup.additionalDocuments);
  return response.data.map(document => additionalDocumentMapper.fromDto(document));
}

/**
 * Get courses.
 * @param {CourseLookupOptions} params FilterParams
 * @returns {Promise<CourseDetail[]>}
 */
async function getCourses(params) {
  const paramsDto = mapCourseLookupOptionsToDto(params);
  const response = await axiosService.get(appUrlsConfig.lookup.courses, { params: paramsDto });
  return response.data.map(dto => courseMapper.mapDetailFromDto(dto));
}

/**
 * Get license statuses.
 * @param {LookupOptions} options Lookup options
 * @returns {Promise<LicenseStatus[]>}
 */
async function getLicenseStatuses(options) {
  const response = await axiosService.get(appUrlsConfig.lookup.licenseStatuses, { params: prepareLookupParams(options) });
  return response.data.map(dto => licenseStatusMapper.fromDto(dto));
}

/**
 * Get registration sources.
 * @returns {Promise<RegistrationSource[]>}
 */
async function getRegistrationSources() {
  const response = await axiosService.get(appUrlsConfig.lookup.registrationSources);
  return response.data.map(dto => lookupMapper.registrationSourceFromDto(dto));
}

/**
 * Get registration sources.
 * @returns {Promise<UserAccountStatus[]>}
 */
async function getUserAccountStatuses() {
  const response = await axiosService.get(appUrlsConfig.lookup.visibilities);
  return response.data.map(dto => lookupMapper.visibilityFromDto(dto));
}

/**
 * Get registration sources.
 * @returns {Promise<Visibility[]>}
 */
async function getVisibilities() {
  const data = [
    {
      name: 'All',
      value: null,
    },
    {
      name: VISIBILITY_VALUE.HIDDEN,
      value: VISIBILITY_NUMERIC_VALUE.HIDDEN,
    },
    {
      name: VISIBILITY_VALUE.UNHIDDEN,
      value: VISIBILITY_NUMERIC_VALUE.UNHIDDEN,
    },
  ];
  return data.map(dto => lookupMapper.visibilityFromDto(dto));
}

/**
 * Get zipcode detail.
 * @param {string} code Zipcode.
 * @returns {Promise<Zip>}
 */
async function getZipcodeDetail(code) {
  const response = await axiosService.get(appUrlsConfig.zip.getDetail(code));
  return zipMapper.fromDto(response.data);
}

export const lookupService = {
  getDocumentTypes,
  getAdditionalDocuments,
  getCourses,
  getLicenseStatuses,
  getRegistrationSources,
  getVisibilities,
  getUserAccountStatuses,
  getZipcodeDetail,
  getCourseStatuses,
};
